import { Box, HBox, VBox } from "@ui/box/Box"
import { Typo } from "@ui/text/Typo"
import type { ReactNode } from "react"
import { toast } from "react-toastify"
import { GD } from "src/GD"
import { FormParser } from "src/view/FormParser"
import { ActionButton, DS } from "../ds/Kit"

type ProfileFormData = {
	name: string
	email: string
	error?: IError
	$__email_disabled?: boolean
	$__decorator?: {
		order: string[]
		gridTemplateColumns: string
	}
}

const twoColumns = { gridTemplateColumns: "repeat(2, minmax(0, 1fr))", "@media (max-width: 600px)": { gridTemplateColumns: "minmax(0, 1fr)" } }

const icon = (path: ReactNode) => (
	<svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>{path}</svg>
)

const SettingsCard = ({ glyph, title, description, tone = "accent", children }: {
	glyph: ReactNode
	title: string
	description: string
	tone?: "accent" | "neutral"
	children: ReactNode
}) => (
	<VBox gap='16px' sx={{ padding: "18px", borderRadius: DS.radius, border: `1px solid ${DS.line}`, background: DS.surface, boxShadow: DS.shadow }}>
		<HBox gap='12px' sx={{ alignItems: "flex-start" }}>
			<Box
				aria-hidden
				sx={{
					width: "36px",
					height: "36px",
					minWidth: "36px",
					borderRadius: "11px",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					background: tone === "accent" ? DS.accentSoft : DS.soft,
					color: tone === "accent" ? DS.accent : DS.muted
				}}
			>
				{glyph}
			</Box>
			<VBox gap='3px'>
				<Typo sx={{ color: DS.ink, fontWeight: 650, fontSize: "15px" }}>{title}</Typo>
				<Typo sx={{ color: DS.muted, fontSize: "13px", lineHeight: 1.45 }}>{description}</Typo>
			</VBox>
		</HBox>
		{children}
	</VBox>
)

export const ProfileSettings = ({ showLogout = false }: { showLogout?: boolean } = {}) => (
	<VBox gap='14px' sx={{ width: "min(620px, 100%)" }}>
		<SettingsCard
			glyph={icon(<><circle cx='12' cy='8' r='4' /><path d='M4 21a8 8 0 0 1 16 0' /></>)}
			title='Profile'
			description='Update the name shown across your account. Your email address cannot be changed.'
		>
			<FormParser<ProfileFormData>
				key='selfProfile'
				fieldsSx={twoColumns}
				primaryButton={{ title: "Save profile" }}
				onRequest={async () => {
					const res = await GD.REQ_GET_MY_PROFILE.request({ include_teams: true })
					if (res.error) return { name: "", email: "", error: res.error }

					return {
						name: res.data.name ?? "",
						email: res.data.email ?? "",
						$__email_disabled: true,
						$__decorator: {
							order: ["name", "email"],
							gridTemplateColumns: "1fr 1fr"
						}
					}
				}}
				onSubmit={async data => {
					const res = await GD.REQ_UPDATE_MY_PROFILE.request({ name: data.name })
					if (res.error) return res

					toast.success("Profile updated")
					return { data: { ...data, name: data.name.trim() } }
				}}
			/>
		</SettingsCard>

		<SettingsCard
			glyph={icon(<><rect x='4' y='11' width='16' height='10' rx='2' /><path d='M8 11V7a4 4 0 0 1 8 0v4' /></>)}
			title='Change password'
			description='Changing your password signs you out on every device. You will need to log in again.'
		>
			<FormParser
				key='selfPassword'
				fieldsSx={twoColumns}
				primaryButton={{ title: "Change password" }}
				onRequest={async () => ({
					old_password: "",
					password: "",
					$__old_password_type: "password",
					$__old_password_label: "Current password",
					$__password_type: "password",
					$__password_label: "New password",
					$__decorator: {
						order: ["old_password", "password"],
						gridTemplateColumns: "1fr 1fr"
					}
				})}
				onSubmit={async data => {
					const res = await GD.REQ_CHANGE_MY_PASSWORD.request({
						old_password: data.old_password,
						password: data.password
					})
					if (res.error) return res

					GD.S_AUTH_LOG_OUT.invoke()
					return res
				}}
			/>
		</SettingsCard>

		<SettingsCard
			tone='neutral'
			glyph={icon(<><circle cx='12' cy='12' r='9' /><circle cx='9' cy='10' r='1' /><circle cx='14' cy='14' r='1' /><circle cx='15' cy='9' r='1' /></>)}
			title='Privacy preferences'
			description='Review the cookies used by Cardpartner and update your preferences.'
		>
			<HBox sx={{ justifyContent: "flex-end" }}>
				<Box
					element='button'
					type='button'
					data-cookie-settings
					sx={{ minHeight: "40px", padding: "0 16px", borderRadius: "12px", fontFamily: "inherit", fontSize: "14px", fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap", background: DS.surface, color: DS.ink, border: `1px solid ${DS.line}`, boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)", "&:hover": { background: DS.soft } }}
				>
					Cookie settings
				</Box>
			</HBox>
		</SettingsCard>

		{showLogout && (
			<SettingsCard
				tone='neutral'
				glyph={icon(<><path d='M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4' /><path d='M16 17l5-5-5-5' /><path d='M21 12H9' /></>)}
				title='Session'
				description='Sign out of Cardpartner on this device.'
			>
				<HBox sx={{ justifyContent: "flex-end" }}>
					<ActionButton onClick={() => GD.S_AUTH_LOG_OUT.invoke()}>Log out</ActionButton>
				</HBox>
			</SettingsCard>
		)}
	</VBox>
)
